import {
	type User,
	type Like,
	type Review as ReviewType,
	type UserImage as UserImageType,
	type Comment,
} from '@prisma/client'
import { type SerializeFrom } from '@remix-run/node'
import { Link, Outlet, useLocation } from '@remix-run/react'
import { formatDistanceToNow } from 'date-fns'
import { createContext, useContext } from 'react'
import { useOptionalUser } from '#app/utils/user.js'
import { DislikeCommentForm } from './dislike-comment-form'
import { DislikeReviewForm } from './dislike-review-form'
import { LikeCommentForm } from './like-comment-form'
import { LikeReviewForm } from './like-review-form'
import { ReportCommentForm } from './report-comment-form'
import { ReportReviewForm } from './report-review-form'
import { StarRating } from './star-rating'
import { Button } from './ui/button'
import UserImage from './user-image'

export const ADD_REVIEW_ENDPOINT = 'add-review'
export const EDIT_REVIEW_ENDPOINT = 'edit-review'
const ADDED_PRODUCT_MESSAGE = 'ADDED PRODUCT'
const EDITED_REVIEW_MESSAGE = 'REVIEW EDITED'
const EDITED_COMMENT_MESSAGE = 'COMMENT EDITED'

interface ReviewContextType {
	addedByUserId: string
}

const ReviewContext = createContext<ReviewContextType | undefined>(undefined)

export const useReviewContext = () => {
	const context = useContext(ReviewContext)
	if (!context) {
		throw new Error('useReviewContext must be used within a ReviewProvider')
	}
	return context
}

export const ReviewProvider = ({
	children,
	addedByUserId,
}: {
	children: React.ReactNode
	addedByUserId: string
}) => {
	return (
		<ReviewContext.Provider value={{ addedByUserId }}>
			{children}
		</ReviewContext.Provider>
	)
}

export const Review = ({
	review,
	children,
}: {
	review: SerializeFrom<
		Pick<
			ReviewType,
			'id' | 'rating' | 'createdAt' | 'content' | 'updatedAt'
		> & {
			likes: Array<Pick<Like, 'type' | 'userId'>>
		} & {
			user:
				| (Pick<User, 'id' | 'name' | 'username'> & {
						image: Pick<UserImageType, 'id'> | null
						_count: { reviews: number; addedProducts: number }
				  })
				| null
		}
	>
	children: React.ReactNode
}) => {
	const user = useOptionalUser()
	const location = useLocation()
	const reviewLikeCount = review
		? review.likes.filter(like => like.type === 'like').length
		: null
	const reviewDislikeCount = review
		? review.likes.filter(like => like.type === 'dislike').length
		: null
	const { addedByUserId } = useReviewContext()
	const totalUserReviews = review.user?._count.reviews
	const totalUserProducts = review.user?._count.addedProducts
	const timeAgo = formatDistanceToNow(new Date(review.createdAt))
	const addedProductMessage = review.user &&
		review.user.id === addedByUserId && (
			<p className="text-xs text-muted-foreground">{ADDED_PRODUCT_MESSAGE}</p>
		)
	const editedReviewMessage = review.updatedAt !== review.createdAt && (
		<p className="text-xs text-muted-foreground">{EDITED_REVIEW_MESSAGE}</p>
	)
	return (
		review &&
		reviewLikeCount !== null &&
		reviewDislikeCount !== null &&
		(location.pathname.endsWith(`${review.id}/${EDIT_REVIEW_ENDPOINT}`) ? (
			<Outlet />
		) : (
			<div key={review.id} className="flex flex-col gap-4 pb-4">
				<div className="flex w-full items-center gap-2">
					<div className="flex flex-1 items-center justify-between">
						{review.user ? (
							<Link
								to={`/users/${review.user && review.user.username}/reviews-added`}
								className="flex items-center gap-2 rounded-lg pr-4 hover:bg-primary-100"
							>
								<UserImage user={review.user} isLink={false} />
								<div>
									<div className="flex gap-1 max-sm:flex-col max-sm:gap-0">
										{addedProductMessage}
										{addedProductMessage && editedReviewMessage && (
											<span className="text-xs text-muted-foreground max-sm:hidden">
												•
											</span>
										)}
										{editedReviewMessage}
									</div>
									<span className="max-sm:text-sm">
										{review.user ? review.user.username : 'User deleted'}
									</span>
									<div className="flex gap-1 text-sm text-muted-foreground">
										<span className="font-bold max-sm:text-xs">
											{totalUserReviews}
										</span>
										<span className="max-sm:text-xs">
											{totalUserReviews === 1 ? 'review' : 'reviews'}
										</span>
										<span className="font-bold max-sm:text-xs">
											{totalUserProducts}
										</span>
										<span className="max-sm:text-xs">
											{totalUserProducts === 1 ? 'product' : 'products'}
										</span>
									</div>
								</div>
							</Link>
						) : (
							<div>
								<span>User deleted</span>
							</div>
						)}
						<div className="flex items-center gap-1">
							<span className="text-xs text-muted-foreground">{timeAgo}</span>
							<ReportReviewForm reviewId={review.id} />
						</div>
					</div>
				</div>
				<div className="col-span-5 flex w-full flex-col gap-4">
					<div className="flex flex-col pl-1">
						<div className="flex justify-between">
							<StarRating
								rating={review.rating}
								clickable={false}
								size="lg"
								filledColor="text-secondary-600"
							/>
						</div>
						{review.content ? (
							<span className="break-words py-1 ">{review.content}</span>
						) : null}
						<div className="flex">
							<LikeReviewForm
								clickable={
									review.user && user && review.user.id === user.id
										? false
										: true
								}
								likeCount={reviewLikeCount}
								reviewId={review.id}
								isLiked={review.likes.some(
									like =>
										user && like.userId === user.id && like.type === 'like',
								)}
							/>
							<DislikeReviewForm
								clickable={
									review.user && user && review.user.id === user.id
										? false
										: true
								}
								dislikeCount={reviewDislikeCount}
								reviewId={review.id}
								isDisliked={review.likes.some(
									like =>
										user && like.userId === user.id && like.type === 'dislike',
								)}
							/>
							<Button variant={'link'} asChild>
								<Link
									to={`${review.id}/${ADD_COMMENT_ENDPOINT}`}
									prefetch="render"
									preventScrollReset={true}
									className="max-sm:text-sm"
								>
									Reply
								</Link>
							</Button>
							{review.user && user && user.id === review.user.id && (
								<Button variant={'link'} asChild>
									<Link
										to={`${review.id}/${EDIT_REVIEW_ENDPOINT}`}
										prefetch="intent"
										preventScrollReset={true}
										className="max-sm:text-sm"
									>
										Edit Review
									</Link>
								</Button>
							)}
						</div>
						{user &&
						location.pathname.endsWith(
							`${review.id}/${ADD_COMMENT_ENDPOINT}`,
						) ? (
							<Outlet />
						) : null}
					</div>
					{children}
				</div>
			</div>
		))
	)
}

const ADD_COMMENT_ENDPOINT = 'add-comment'
const EDIT_COMMENT_ENDPOINT = 'edit-comment'

Review.Comments = function Comments({
	comments,
}: {
	comments: SerializeFrom<
		Array<
			Pick<
				Comment,
				'id' | 'content' | 'reviewId' | 'createdAt' | 'updatedAt'
			> & {
				likes: Array<Pick<Like, 'type' | 'userId'>>
			} & {
				user:
					| (Pick<User, 'id' | 'name' | 'username'> & {
							image: Pick<UserImageType, 'id'> | null
							_count: { reviews: number; addedProducts: number }
					  })
					| null
			}
		>
	> | null
}) {
	const user = useOptionalUser()
	const location = useLocation()
	const { addedByUserId } = useReviewContext()

	return (
		<div className="flex flex-col gap-6">
			{comments
				? comments.map(comment => {
						const commentLikeCount = comment.likes.filter(
							like => like.type === 'like',
						).length
						const commentDislikeCount = comment.likes.filter(
							like => like.type === 'dislike',
						).length
						const date = new Date(comment.createdAt)
						const timeAgo = formatDistanceToNow(date)
						const totalUserReviews = comment.user?._count.reviews
						const totalUserProducts = comment.user?._count.addedProducts
						const addedProductMessage = comment.user &&
							comment.user.id === addedByUserId && (
								<p className="text-xs text-muted-foreground">
									{ADDED_PRODUCT_MESSAGE}
								</p>
							)
						const editedCommentMessage = comment.updatedAt !==
							comment.createdAt && (
							<p className="text-xs text-muted-foreground">
								{EDITED_COMMENT_MESSAGE}
							</p>
						)
						return location.pathname.endsWith(
							`${comment.reviewId}/${comment.id}/${EDIT_COMMENT_ENDPOINT}`,
						) ? (
							<Outlet />
						) : (
							<div
								key={comment.id}
								className="flex flex-col gap-4 py-2 max-md:pl-16 max-sm:pl-8 md:pl-24"
							>
								<div className="flex w-full gap-2">
									<div className="flex flex-1 items-center justify-between">
										{comment.user ? (
											<Link
												to={`/users/${comment.user.username}/reviews-added`}
												className="flex items-center gap-2 rounded-lg pr-4 hover:bg-primary-100"
											>
												<UserImage user={comment.user} isLink={false} />
												<div>
													<div className="flex gap-1 max-sm:flex-col max-sm:gap-0">
														{addedProductMessage}
														{addedProductMessage && editedCommentMessage && (
															<span className="text-xs text-muted-foreground max-sm:hidden">
																•
															</span>
														)}
														{editedCommentMessage}
													</div>
													<span className="max-sm:text-sm">
														{comment.user
															? comment.user.username
															: 'User deleted'}
													</span>
													<div className="flex gap-1 text-sm text-muted-foreground">
														<span className="font-bold max-sm:text-xs">
															{totalUserReviews}
														</span>
														<span className="max-sm:text-xs">
															{totalUserReviews === 1 ? 'review' : 'reviews'}
														</span>
														<span className="font-bold max-sm:text-xs">
															{totalUserProducts}
														</span>
														<span className="max-sm:text-xs">
															{totalUserProducts === 1 ? 'product' : 'products'}
														</span>
													</div>
												</div>
											</Link>
										) : (
											<div>
												<span>User deleted</span>
											</div>
										)}
										<div className="flex items-center gap-1">
											<span className="text-xs text-muted-foreground">
												{timeAgo}
											</span>
											<ReportCommentForm commentId={comment.id} />
										</div>
									</div>
								</div>
								<div className="flex flex-col">
									{comment.content ? (
										<span className="break-words py-2">{comment.content}</span>
									) : null}
									<div className="flex">
										<LikeCommentForm
											clickable={
												comment.user && user && comment.user.id === user.id
													? false
													: true
											}
											likeCount={commentLikeCount}
											commentId={comment.id}
											isLiked={comment.likes.some(
												like =>
													user &&
													like.userId === user.id &&
													like.type === 'like',
											)}
										/>
										<DislikeCommentForm
											clickable={
												comment.user && user && comment.user.id === user.id
													? false
													: true
											}
											dislikeCount={commentDislikeCount}
											commentId={comment.id}
											isDisliked={comment.likes.some(
												like =>
													user &&
													like.userId === user.id &&
													like.type === 'dislike',
											)}
										/>
										{comment.user && user && user.id === comment.user.id && (
											<Button variant={'link'} asChild>
												<Link
													to={`${comment.reviewId}/${comment.id}/${EDIT_COMMENT_ENDPOINT}`}
													prefetch="intent"
													preventScrollReset={true}
													className="max-sm:text-sm"
												>
													Edit Comment
												</Link>
											</Button>
										)}
									</div>
								</div>
							</div>
						)
					})
				: null}
		</div>
	)
}
